import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Tag, 
  Plus, 
  X, 
  Search,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { tagService, UserTag, SystemTag } from '@/services/tagService';
import { useToast } from '@/hooks/use-toast';

interface DataTaggingComponentProps {
  selectedTags: string[];
  onTagsChange: (tagIds: string[]) => void;
  dataType: 'metric' | 'material' | 'carbon' | 'esg';
  dataId?: string;
  className?: string;
  showCreateTag?: boolean;
}

export const DataTaggingComponent: React.FC<DataTaggingComponentProps> = ({
  selectedTags,
  onTagsChange,
  dataType,
  dataId,
  className = '',
  showCreateTag = true
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tags, setTags] = useState<(UserTag | SystemTag)[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [popoverView, setPopoverView] = useState<'list' | 'create'>('list');
  const [newTagForm, setNewTagForm] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    category: 'Custom'
  });

  useEffect(() => {
    if (user) {
      loadTags();
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      loadTags();
    }
  }, [isOpen]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const allTags = await tagService.getAllTags(user.id);
      setTags(allTags);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId: string) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    
    onTagsChange(newSelectedTags);
  };

  const removeTag = (tagId: string) => {
    const newSelectedTags = selectedTags.filter(id => id !== tagId);
    onTagsChange(newSelectedTags);
  };

  const createTag = async () => {
    try {
      if (!newTagForm.name.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Tag name is required',
          variant: 'destructive'
        });
        return;
      }

      const newTag = await tagService.createTag(user.id, {
        name: newTagForm.name.trim(),
        description: newTagForm.description.trim() || undefined,
        color: newTagForm.color,
        category: newTagForm.category
      });

      // Add the new tag to selected tags
      onTagsChange([...selectedTags, newTag.id]);

      // Reset form and return to list view
      setNewTagForm({
        name: '',
        description: '',
        color: '#3B82F6',
        category: 'Custom'
      });
      setSearchTerm('');
      setPopoverView('list');

      // Reload tags in background
      await loadTags();

      toast({
        title: 'Success',
        description: 'Tag created and applied successfully',
      });
    } catch (error) {
      console.error('Error creating tag:', error);
      toast({
        title: 'Error',
        description: 'Failed to create tag',
        variant: 'destructive'
      });
    }
  };

  const saveTagsToData = async () => {
    if (!dataId || selectedTags.length === 0) return;

    try {
      await tagService.tagData(user.id, dataType, dataId, selectedTags);
      toast({
        title: 'Success',
        description: 'Tags saved to data successfully',
      });
    } catch (error) {
      console.error('Error saving tags to data:', error);
      toast({
        title: 'Error',
        description: 'Failed to save tags to data',
        variant: 'destructive'
      });
    }
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tag.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const selectedTagsData = tags.filter(tag => selectedTags.includes(tag.id));

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Selected Tags Display removed to avoid duplicate listing; parent form shows selection */}

      {/* Tag Selection */}
      <div className="flex items-center gap-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              title="Add tags"
            >
              <Tag className="h-4 w-4" />
              Add Tags
            </Button>
          </PopoverTrigger>
          
          <PopoverContent className="w-80 p-0" align="start">
            {popoverView === 'list' ? (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Select Tags</h4>
                    {showCreateTag && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPopoverView('create')}
                        className="h-6 px-2 text-xs"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        New Tag
                      </Button>
                    )}
                  </div>
                  
                  {/* Search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search tags..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                {/* Tags List */}
                <ScrollArea className="max-h-64">
                  <div className="p-4 space-y-2">
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                        <p className="text-sm text-gray-600 mt-2">Loading tags...</p>
                      </div>
                    ) : tags.length === 0 ? (
                      <div className="text-center py-6">
                        <Tag className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 font-medium mb-2">No tags yet</p>
                        <p className="text-xs text-gray-500 mb-3">Create your first tag to get started</p>
                        {showCreateTag && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPopoverView('create')}
                            className="w-full text-xs"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Create First Tag
                          </Button>
                        )}
                      </div>
                    ) : filteredTags.length === 0 ? (
                      <div className="text-center py-4">
                        <Tag className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No tags match your search</p>
                      </div>
                    ) : (
                      filteredTags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleTagToggle(tag.id)}
                        >
                          <Checkbox
                            checked={selectedTags.includes(tag.id)}
                            onChange={() => handleTagToggle(tag.id)}
                            className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{tag.name}</div>
                              {tag.description && (
                                <div className="text-xs text-gray-500 truncate">{tag.description}</div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Badge variant="outline" className="text-xs">
                              {tag.category}
                            </Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <>
                <div className="p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPopoverView('list');
                        setNewTagForm({
                          name: '',
                          description: '',
                          color: '#3B82F6',
                          category: 'Custom'
                        });
                      }}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-medium">Create New Tag</h3>
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <Label htmlFor="tag-name">Tag Name *</Label>
                    <Input
                      id="tag-name"
                      placeholder="Enter tag name"
                      value={newTagForm.name}
                      onChange={(e) => setNewTagForm(prev => ({ ...prev, name: e.target.value }))}
                      maxLength={50}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tag-description">Description</Label>
                    <Input
                      id="tag-description"
                      placeholder="Optional description"
                      value={newTagForm.description}
                      onChange={(e) => setNewTagForm(prev => ({ ...prev, description: e.target.value }))}
                      maxLength={200}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tag-category">Category</Label>
                      <select
                        id="tag-category"
                        value={newTagForm.category}
                        onChange={(e) => setNewTagForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="Project">Project</option>
                        <option value="Department">Department</option>
                        <option value="Location">Location</option>
                        <option value="Supplier">Supplier</option>
                        <option value="Process">Process</option>
                        <option value="Product">Product</option>
                        <option value="Campaign">Campaign</option>
                        <option value="Initiative">Initiative</option>
                        <option value="Audit">Audit</option>
                        <option value="Compliance">Compliance</option>
                        <option value="Risk">Risk</option>
                        <option value="Opportunity">Opportunity</option>
                        <option value="Custom">Custom</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="tag-color">Color</Label>
                      <div className="grid grid-cols-6 gap-1 mt-2">
                        {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                              newTagForm.color === color ? 'border-gray-800 scale-110' : 'border-gray-300 hover:scale-105'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => setNewTagForm(prev => ({ ...prev, color }))}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setPopoverView('list');
                        setNewTagForm({
                          name: '',
                          description: '',
                          color: '#3B82F6',
                          category: 'Custom'
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={createTag} disabled={!newTagForm.name.trim()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Tag
                    </Button>
                  </div>
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>

        {/* Save Tags Button (only show when dataId is provided) */}
        {dataId && selectedTags.length > 0 && (
          <Button
            size="sm"
            onClick={saveTagsToData}
            className="flex items-center gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Save Tags
          </Button>
        )}
      </div>
    </div>
  );
};
